import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Icon } from "./Icon";

export type ActionMenuControls = {
  /** Close the menu; `restoreFocus` returns focus to the trigger once it is enabled. */
  close: (restoreFocus?: boolean) => void;
  /** Keep the menu open and focus it again, for a choice that was not saved. */
  focus: () => void;
};

/**
 * A board action that opens a small menu beside its trigger. Outside pointer
 * presses, blur and Escape dismiss it, arrow keys move between its items, and
 * a closed menu returns focus to the trigger when a choice asks for it.
 */
export function TodayActionMenu({
  className,
  label,
  triggerLabel,
  buttonClassName = "tb-button",
  triggerTitle,
  disabled,
  menuLabel,
  width = 260,
  children,
}: {
  className?: string;
  label: ReactNode;
  /** Accessible name for the trigger when the visible label is not patient-specific. */
  triggerLabel?: string;
  buttonClassName?: string;
  triggerTitle?: string;
  disabled: boolean;
  menuLabel: string;
  width?: number;
  children: (menu: ActionMenuControls) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, width, above: false });
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const returnFocus = useRef(false);
  const close = (restoreFocus = false) => {
    returnFocus.current = restoreFocus;
    setOpen(false);
  };
  useEffect(() => {
    if (!open && !disabled && returnFocus.current) {
      returnFocus.current = false;
      trigger.current?.focus();
    }
  }, [open, disabled]);
  useEffect(() => {
    if (!open) return;
    const bounds = trigger.current?.getBoundingClientRect();
    if (bounds) {
      const menuWidth = Math.min(width, window.innerWidth - 24);
      setPosition({
        width: menuWidth,
        left: Math.max(
          12 - bounds.left,
          Math.min(0, window.innerWidth - bounds.left - menuWidth - 12),
        ),
        above: window.innerHeight - bounds.bottom < 220 && bounds.top > 220,
      });
    }
    (
      menu.current?.querySelector<HTMLButtonElement>("button:not(:disabled)") ?? menu.current
    )?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !container.current?.contains(event.target))
        setOpen(false);
    };
    const resize = () => setOpen(false);
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", resize);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", resize);
    };
  }, [open, width]);

  return (
    <div className={`tb-action-menu${className ? ` ${className}` : ""}`} ref={container}>
      <button
        ref={trigger}
        type="button"
        className={buttonClassName}
        title={triggerTitle}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        {...(triggerLabel ? { "aria-label": triggerLabel } : {})}
        onClick={() => setOpen(!open)}
      >
        {label}
        <Icon name="chevron" />
      </button>
      {open ? (
        <div
          id={id}
          ref={menu}
          className="tb-action-menu-list"
          role="menu"
          tabIndex={-1}
          aria-label={menuLabel}
          style={{
            left: position.left,
            width: position.width,
            ...(position.above ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }),
          }}
          onBlur={(event) => {
            if (
              event.relatedTarget instanceof Node &&
              !container.current?.contains(event.relatedTarget)
            )
              close();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              close(true);
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const options = [
              ...(menu.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []),
            ];
            if (!options.length) return;
            const active = document.activeElement;
            const index = active instanceof HTMLButtonElement ? options.indexOf(active) : -1;
            const next =
              event.key === "Home"
                ? 0
                : event.key === "End"
                  ? options.length - 1
                  : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) %
                    options.length;
            options[next]?.focus();
          }}
        >
          {children({ close, focus: () => menu.current?.focus() })}
        </div>
      ) : null}
    </div>
  );
}
